/**
 * ApplicationGroup Service
 * API integration for Application Group dashboard (Grupo de Aplicação)
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de visualização consolidada de aplicações
 */

import apiClient from './apiClient';
import { ApiResponse } from '../types/common';
import {
  ConsolidatedData,
  ConsolidatedDataResponse,
} from '../types/applicationGroup';

// Re-export types for convenience
export * from '../types/applicationGroup';

// ============================================================================
// RESPONSE TYPE ADAPTERS
// ============================================================================

/**
 * Unwrap API response data
 * httpClient returns ApiResponse<T> which has optional data field
 */
function unwrapData<T>(response: ApiResponse<T>): T {
  if (response.data !== undefined) {
    return response.data;
  }
  return response as unknown as T;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class ApplicationGroupService {
  private baseUrl = '/application-groups';

  // ==========================================================================
  // CONSOLIDATED DATA METHODS
  // ==========================================================================

  /**
   * Get consolidated application data for a company
   */
  async getConsolidatedByCompany(companyId: number): Promise<ConsolidatedData> {
    const response = await apiClient.get<ConsolidatedDataResponse>(
      `${this.baseUrl}/company/${companyId}/consolidated`
    );
    // response = { message, data: {...}, totals }
    // We need to access the nested data and totals from the response
    const apiResponse = response as unknown as ConsolidatedDataResponse;

    return {
      companyId: apiResponse.data?.companyId || companyId,
      companyName: apiResponse.data?.companyName || `Empresa ${companyId}`,
      risks: apiResponse.data?.risks || [],
      exams: apiResponse.data?.exams || [],
      sources: apiResponse.data?.sources || [],
      totals: apiResponse.totals || { risks: 0, exams: 0, sources: 0, total: 0 },
    };
  }

  // ==========================================================================
  // HEALTH CHECK
  // ==========================================================================

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ status: string }>(
        `${this.baseUrl}/health`
      );
      const data = unwrapData(response);
      return data.status === 'OK';
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const applicationGroupService = new ApplicationGroupService();
export default applicationGroupService;
